import React, { useState, useEffect } from 'react';
import { Box, CircularProgress, Typography } from '@mui/material';
import { AutoAwesome, Psychology, Search, Description, Groups, WorkHistory } from '@mui/icons-material';

const LOADING_MESSAGES = [
  { text: 'Talking to recruiters...', icon: Groups },
  { text: 'Fetching job descriptions...', icon: WorkHistory },
  { text: 'Analyzing resumes...', icon: Description },
  { text: 'Searching candidate database...', icon: Search },
  { text: 'Processing your request...', icon: Psychology },
  { text: 'Generating insights...', icon: AutoAwesome },
  { text: 'Reviewing requirements...', icon: Description },
  { text: 'Matching skills...', icon: Search },
  { text: 'Consulting HR policies...', icon: Groups },
  { text: 'Preparing recommendations...', icon: AutoAwesome },
];

interface AgentContentLoaderProps {
  sx?: any;
}

const AgentContentLoader: React.FC<AgentContentLoaderProps> = ({ sx }) => {
  const [currentMessageIndex, setCurrentMessageIndex] = useState(0);
  const [fadeIn, setFadeIn] = useState(true);

  useEffect(() => {
    const intervalId = setInterval(() => {
      setFadeIn(false);
      
      setTimeout(() => {
        setCurrentMessageIndex((prev) => (prev + 1) % LOADING_MESSAGES.length);
        setFadeIn(true);
      }, 300);
    }, 2500);

    return () => clearInterval(intervalId);
  }, []);

  const currentMessage = LOADING_MESSAGES[currentMessageIndex];
  const IconComponent = currentMessage.icon;

  return (
    <Box
      sx={{
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        justifyContent: 'center',
        height: '100%',
        gap: 3,
        background: 'linear-gradient(135deg, #f8faff 0%, #e8f0ff 100%)',
        ...sx,
      }}
    >
      {/* Animated Icon Container */}
      <Box
        sx={{
          position: 'relative',
          width: 120,
          height: 120,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
        }}
      >
        {/* Outer rotating circle */}
        <Box
          sx={{
            position: 'absolute',
            width: '100%',
            height: '100%',
            borderRadius: '50%',
            border: '3px solid rgba(79, 140, 255, 0.1)',
            borderTopColor: 'var(--primary-blue)',
            animation: 'rotate 2s linear infinite',
            '@keyframes rotate': {
              '0%': { transform: 'rotate(0deg)' },
              '100%': { transform: 'rotate(360deg)' },
            },
          }}
        />
        
        {/* Inner pulsing circle */}
        <Box
          sx={{
            position: 'absolute',
            width: '80%',
            height: '80%',
            borderRadius: '50%',
            background: 'linear-gradient(135deg, #4f8cff 0%, #667eea 100%)',
            animation: 'pulse 2s ease-in-out infinite',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            boxShadow: '0 8px 32px rgba(79, 140, 255, 0.3)',
            '@keyframes pulse': {
              '0%, 100%': { 
                transform: 'scale(1)',
                boxShadow: '0 8px 32px rgba(79, 140, 255, 0.3)',
              },
              '50%': { 
                transform: 'scale(1.05)',
                boxShadow: '0 12px 40px rgba(79, 140, 255, 0.4)',
              },
            },
          }}
        >
          <IconComponent
            sx={{
              fontSize: 40,
              color: '#fff',
              opacity: fadeIn ? 1 : 0,
              transition: 'opacity 0.3s ease-in-out',
            }}
          />
        </Box>
      </Box>

      {/* Loading Message */}
      <Box
        sx={{
          textAlign: 'center',
          maxWidth: 400,
          px: 2,
        }}
      >
        <Typography
          variant="h6"
          sx={{
            fontFamily: 'var(--font-heading)',
            fontWeight: 600,
            color: 'var(--primary-blue)',
            mb: 1,
            opacity: fadeIn ? 1 : 0,
            transform: fadeIn ? 'translateY(0)' : 'translateY(-10px)',
            transition: 'all 0.3s ease-in-out',
          }}
        >
          {currentMessage.text}
        </Typography>
        
        <Typography
          variant="body2"
          sx={{
            fontFamily: 'var(--font-body)',
            color: 'var(--dark-gray)',
            opacity: 0.7,
          }}
        >
          AI is working on your request
        </Typography>
      </Box>

      {/* Progress dots */}
      <Box
        sx={{
          display: 'flex',
          gap: 1,
          mt: 1,
        }}
      >
        {[0, 1, 2].map((index) => (
          <Box
            key={index}
            sx={{
              width: 8,
              height: 8,
              borderRadius: '50%',
              bgcolor: 'var(--primary-blue)',
              opacity: 0.3,
              animation: `dotPulse 1.5s ease-in-out ${index * 0.2}s infinite`,
              '@keyframes dotPulse': {
                '0%, 100%': { opacity: 0.3, transform: 'scale(1)' },
                '50%': { opacity: 1, transform: 'scale(1.2)' },
              },
            }}
          />
        ))}
      </Box>
    </Box>
  );
};

export default AgentContentLoader;

